import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import Welcome from "../pages/Welcome";
import Home from "../pages/Home";
import Scan from "../pages/Scan";
import MyGallery from "../pages/MyGallery";
import MyOrder from "../pages/MyOrder";
import Product from "../pages/Product";
import PrintFrameSelection from "../pages/PrintFrameSelection";
import QrPayment from "../pages/QrPayment";
import CounterPayment from "../pages/CounterPayment";
import Success from "../pages/Success";
import GlobalPinHandler from "../components/GlobalPinHandler";
import KioskSessionManager from "../components/KioskSessionManager";
import PWAInstallPrompt from "../components/PWAInstallPrompt";

/** Keep in sync with stack animation duration in pageMotion.css. */
const ROUTE_TRANSITION_MS = 580;

/**
 * Flow depth so UI "back" navigations animate like history POP —
 * same approach as Mosida-Level1-Canvas.
 */
function routeDepth(pathname = "") {
  const path = String(pathname || "");
  if (!path || path === "/") return 0;
  if (path === "/login") return 1;
  // /scan is above sign-in so Back uses the reverse (back) transition
  if (path === "/scan") return 2;
  // Gallery ↔ orders are siblings → same forward motion both ways
  if (path === "/my-gallery" || path === "/my-order") return 3;
  if (path === "/product" || path === "/print-frame-selection") return 4;
  if (path === "/qr-payment") return 5;
  if (path === "/counter-payment" || path === "/success") return 6;
  return 0;
}

function isBackNavigation(fromPath, toPath, navigationType) {
  // Same depth (e.g. gallery ↔ orders): always forward, like tab switches
  if (routeDepth(fromPath) === routeDepth(toPath)) return false;
  if (navigationType === "POP") return true;
  return routeDepth(toPath) < routeDepth(fromPath);
}

function renderAppRoutes(location) {
  return (
    <Routes location={location}>
      <Route path="/" element={<Welcome />} />
      <Route path="/login" element={<Home />} />
      <Route path="/scan" element={<Scan />} />
      <Route path="/my-gallery" element={<MyGallery />} />
      <Route path="/my-order" element={<MyOrder />} />
      <Route path="/product" element={<Product />} />
      <Route path="/print-frame-selection" element={<PrintFrameSelection />} />
      <Route path="/qr-payment" element={<QrPayment />} />
      <Route path="/counter-payment" element={<CounterPayment />} />
      <Route path="/success" element={<Success />} />
    </Routes>
  );
}

/**
 * Stack transition: previous page stays underneath while the new one slides
 * in (forward) or the current one slides away (back).
 */
function AnimatedRoutes() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const displayedRef = useRef(location);
  const [displayed, setDisplayed] = useState(location);
  const [outgoing, setOutgoing] = useState(null);
  const [direction, setDirection] = useState("forward");

  useLayoutEffect(() => {
    if (location.pathname === displayedRef.current.pathname) {
      displayedRef.current = location;
      setDisplayed(location);
      return;
    }

    const goingBack = isBackNavigation(
      displayedRef.current.pathname,
      location.pathname,
      navigationType
    );
    setDirection(goingBack ? "back" : "forward");
    setOutgoing(displayedRef.current);
    displayedRef.current = location;
    setDisplayed(location);
  }, [location, navigationType]);

  useEffect(() => {
    if (!outgoing) return undefined;
    const timer = window.setTimeout(() => {
      setOutgoing(null);
    }, ROUTE_TRANSITION_MS);
    return () => window.clearTimeout(timer);
  }, [outgoing, displayed.pathname]);

  return (
    <>
      <div className="routeTransitionHost">
        {outgoing ? (
          <div
            key={`out-${outgoing.pathname}`}
            className={`routeTransition routeTransition--exit-${direction}`}
            aria-hidden="true"
          >
            {renderAppRoutes(outgoing)}
          </div>
        ) : null}
        <div
          key={`in-${displayed.pathname}`}
          className={[
            "routeTransition",
            outgoing ? `routeTransition--enter-${direction}` : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {renderAppRoutes(displayed)}
        </div>
      </div>
      <GlobalPinHandler />
      <KioskSessionManager />
      <PWAInstallPrompt />
    </>
  );
}

const AppRoutes = () => (
  <Router>
    <AnimatedRoutes />
  </Router>
);

export default AppRoutes;
